import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Heading,
  SimpleGrid,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { SupplierInquiry } from '@texas/api/endpoints/inquiryApi';
import { Icons } from '@texas/components/shared/Icons';
import { useMetadata } from '@texas/hooks/metadata/useMetadata';
import { ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const detailGridColumns =
  'repeat(auto-fit, minmax(min(100%, max(18rem, calc((100% - 3 * var(--chakra-space-3)) / 4))), 1fr))';

interface Props {
  inquiry: SupplierInquiry;
  isAccepted: boolean;
  onEdit: () => void;
}

function DetailField({ label, value }: { label: string; value: ReactNode }) {
  return (
    <Flex align="baseline" justify="space-between" gap={2} minW={0}>
      <Text
        color="inquiryOverview.mutedText"
        fontSize="sm"
        minW={0}
        overflowWrap="anywhere"
      >
        {label}
      </Text>
      <Text
        color="inquiryOverview.text"
        fontSize="sm"
        fontWeight="medium"
        textAlign="right"
        minW={0}
        overflowWrap="anywhere"
      >
        {value}
      </Text>
    </Flex>
  );
}

function DetailSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <AccordionItem
      border="none"
      borderRadius="lg"
      overflow="hidden"
      mb={3}
      boxShadow="sm"
    >
      <h3>
        <AccordionButton
          bg="inquiryOverview.raisedSurface"
          px={4}
          py={3}
          _hover={{ bg: 'inquiryOverview.raisedSurface' }}
        >
          <Box flex={1} textAlign="left" fontSize="sm" fontWeight="semibold">
            {title}
          </Box>
          <AccordionIcon />
        </AccordionButton>
      </h3>
      <AccordionPanel px={4} py={4}>
        {children}
      </AccordionPanel>
    </AccordionItem>
  );
}

export function InquiryChargesReadOnly({ inquiry, isAccepted, onEdit }: Props) {
  const { t } = useTranslation();
  const {
    useCurrenciesData: { findCurrency },
    currenciesData: [, refetchCurrencies],
    handlingFeesData: [handlingFees, getHandlingFees],
    freightCostsData: [freightCosts, getFreightCosts],
    customsCodesData: [, getCustomsCodes],
    useCustomsCodesData: { customsCodesInBranch },
  } = useMetadata();

  useEffect(() => {
    refetchCurrencies();
    getHandlingFees();
    getFreightCosts();
    getCustomsCodes();
  }, [refetchCurrencies, getHandlingFees, getFreightCosts, getCustomsCodes]);

  const notSet = t('inquiry.notSet');
  const currencyCode = findCurrency(inquiry.currencyId)?.code ?? notSet;
  const handlingFeeValue = handlingFees.find(
    (fee) => fee.id === inquiry.handlingFeeId,
  )?.value;
  const handlingFee =
    handlingFeeValue != null ? `${handlingFeeValue}%` : notSet;
  const freightCostValue = freightCosts.find(
    (cost) => cost.id === inquiry.freightCostId,
  )?.value;
  const freightCost =
    freightCostValue != null ? `${freightCostValue}%` : notSet;
  const customsCodeItem = inquiry.customsCodeId
    ? customsCodesInBranch(inquiry.branchId).find(
        (code) => code.id === inquiry.customsCodeId,
      )
    : null;
  const customsCode = customsCodeItem
    ? `${customsCodeItem.identifier} — ${customsCodeItem.name}`
    : notSet;
  const hasDevelopmentCost =
    inquiry.developmentCost != null ||
    inquiry.developmentRefundQuantity != null ||
    inquiry.developmentRefundTimeMonths != null;
  const leadTime = (value: number | null) =>
    value != null ? `${value} ${t('inquiry.days')}` : notSet;

  return (
    <Card
      variant="dark"
      bg="inquiryOverview.surface"
      border="1px solid"
      borderColor="inquiryOverview.softBorder"
      boxShadow="sm"
    >
      <CardHeader px={5} pt={4} pb={2}>
        <Flex justify="space-between" align="center" gap={3}>
          <Heading size="xs" textTransform="uppercase" letterSpacing="wide">
            {t('inquiry.details')}
          </Heading>
          <Tooltip label={t('inquiry.lockedReady')} isDisabled={!isAccepted}>
            <span>
              <Button
                size="sm"
                variant="entityDirectorySecondary"
                leftIcon={<Icons.Pencil boxSize={3.5} />}
                isDisabled={isAccepted}
                onClick={onEdit}
              >
                {t('general.edit')}
              </Button>
            </span>
          </Tooltip>
        </Flex>
      </CardHeader>
      <CardBody px={5} pt={0} pb={5}>
        <Accordion allowMultiple={true} defaultIndex={[0, 1, 2, 3]}>
          <DetailSection title={t('general.pricing')}>
            <SimpleGrid
              templateColumns={detailGridColumns}
              spacingX={3}
              spacingY={4}
            >
              <DetailField label={t('inquiry.currency')} value={currencyCode} />
              <DetailField
                label={t('inquiry.exchangeRate')}
                value={inquiry.exchangeRate}
              />
              <DetailField
                label={t('inquiry.sampleCharge')}
                value={`${inquiry.sampleCharge} ${currencyCode}`}
              />
              <DetailField
                label={t('inquiry.mouldCharge')}
                value={`${inquiry.mouldCharge} ${currencyCode}`}
              />
              <DetailField
                label={t('inquiry.surcharge')}
                value={`${inquiry.surcharge} ${currencyCode}`}
              />
              <DetailField
                label={t('inquiry.handlingFee')}
                value={handlingFee}
              />
              <DetailField
                label={t('inquiry.freightCost')}
                value={freightCost}
              />
            </SimpleGrid>
          </DetailSection>
          <DetailSection title={t('inquiry.leadTimes')}>
            <SimpleGrid
              templateColumns={detailGridColumns}
              spacingX={3}
              spacingY={4}
            >
              <DetailField
                label={t('inquiry.sampleLeadTime')}
                value={leadTime(inquiry.sampleLeadTime)}
              />
              <DetailField
                label={t('inquiry.bulkLeadTime')}
                value={leadTime(inquiry.bulkLeadTime)}
              />
              <DetailField
                label={t('inquiry.shipmentLeadTime')}
                value={leadTime(inquiry.shipmentLeadTime)}
              />
            </SimpleGrid>
          </DetailSection>
          <DetailSection title={t('inquiry.orderConditions')}>
            <SimpleGrid
              templateColumns={detailGridColumns}
              spacingX={3}
              spacingY={4}
            >
              <DetailField
                label={t('inquiry.mpq')}
                value={inquiry.mpqQuantity ?? notSet}
              />
              <DetailField
                label={t('inquiry.customsCode')}
                value={customsCode}
              />
            </SimpleGrid>
          </DetailSection>
          {hasDevelopmentCost && (
            <DetailSection title={t('inquiry.developmentCost')}>
              <SimpleGrid
                templateColumns={detailGridColumns}
                spacingX={3}
                spacingY={4}
              >
                {inquiry.developmentCost != null && (
                  <DetailField
                    label={t('inquiry.developmentCost')}
                    value={`${inquiry.developmentCost} ${currencyCode}`}
                  />
                )}
                {inquiry.developmentRefundQuantity != null && (
                  <DetailField
                    label={t('inquiry.developmentRefundQuantity')}
                    value={inquiry.developmentRefundQuantity}
                  />
                )}
                {inquiry.developmentRefundTimeMonths != null && (
                  <DetailField
                    label={t('inquiry.developmentRefundTimeMonths')}
                    value={`${inquiry.developmentRefundTimeMonths} ${t(
                      'inquiry.months',
                    )}`}
                  />
                )}
              </SimpleGrid>
            </DetailSection>
          )}
        </Accordion>
        <Flex
          align="center"
          gap={2}
          bg="inquiryOverview.raisedSurface"
          border="1px solid"
          borderColor="inquiryOverview.softBorder"
          borderRadius="md"
          color="inquiryOverview.mutedText"
          px={4}
          py={3}
          fontSize="xs"
        >
          <Icons.InformationOutline boxSize={4} flexShrink={0} />
          <Text>
            {t('inquiry.valuesShownIn', {
              currency: currencyCode,
              rate: inquiry.exchangeRate,
            })}
          </Text>
        </Flex>
      </CardBody>
    </Card>
  );
}
